import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import { AuthContext } from '@/modules/auth/authContext'
import {
    SettingsSearchDialog,
    useSettingsSearchShortcut,
} from './SettingsSearchDialog'

type SettingsSearchContextValue = {
    openSearch: () => void
}

const SettingsSearchContext = createContext<SettingsSearchContextValue | null>(
    null,
)

export const useSettingsSearch = () => {
    const context = useContext(SettingsSearchContext)
    if (!context) {
        throw new Error(
            'useSettingsSearch must be used within SettingsSearchProvider',
        )
    }
    return context
}

type SettingsSearchProviderProps = {
    children: React.ReactNode
}

export const SettingsSearchProvider = ({
    children,
}: SettingsSearchProviderProps) => {
    const { isSuperUser, client } = useContext(AuthContext)
    const [open, setOpen] = useState(false)
    const isOrganicClient = client?.leadSource === 'ORGANIC'
    const openSearch = useCallback(() => setOpen(true), [])

    useSettingsSearchShortcut(openSearch, true)

    const value = useMemo(() => ({ openSearch }), [openSearch])

    return (
        <SettingsSearchContext.Provider value={value}>
            {children}
            <SettingsSearchDialog
                open={open}
                onOpenChange={setOpen}
                isSuperUser={isSuperUser}
                isOrganicClient={isOrganicClient}
            />
        </SettingsSearchContext.Provider>
    )
}
